import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

/**
 * Small "How this works" info button used in page headers.
 *
 * Props:
 *  - title:    short page title (e.g. "Field View")
 *  - purpose:  one plain-English sentence on what the page is for
 *  - actions:  [{ label, desc }] — what the main buttons do
 *  - nextStep: plain-English "what to do next" (optional)
 *  - testId:   unique data-testid suffix (required for unique buttons)
 *  - compact:  icon-only trigger (default true on mobile)
 */
export default function PageHelp({ title, purpose, actions = [], nextStep, testId = 'page' }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-muted-foreground gap-1 h-8 px-2"
        onClick={() => setOpen(true)}
        data-testid={`help-button-${testId}`}
        aria-label="How this works"
      >
        <HelpCircle className="h-4 w-4" />
        <span className="hidden sm:inline">How this works</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`help-dialog-${testId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              {title}
            </DialogTitle>
            {purpose && <DialogDescription>{purpose}</DialogDescription>}
          </DialogHeader>

          {actions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                What the buttons do
              </p>
              <ul className="space-y-2 text-sm">
                {actions.map((a, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-medium text-foreground whitespace-nowrap">{a.label}</span>
                    <span className="text-muted-foreground">— {a.desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nextStep && (
            <div className="rounded-md bg-primary/5 border border-primary/15 p-3 text-sm">
              <span className="font-semibold">Next: </span>
              <span className="text-muted-foreground">{nextStep}</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
